import React, { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import MessageBox from "./MessageBox";
import { resetForm, subscribeNewsletterUser } from "../Functions/Utilities";
import EnvelopeIcon from "Assets/img/envelope_icon.svg";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../hooks/useLanguage";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay: 0.2 },
};

const NewsletterPromo = () => {
  const { t } = useTranslation(['common', 'blog']);
  const { getLocalizedPath } = useLanguage();
  const [formStatus, setFormStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('blog:newsletter.invalidEmail', 'Neplatný email. Prosím zadajte platný email.'))
      .required(t('blog:newsletter.required', 'Toto pole je povinné')),
  });

  // Get appropriate variant based on status message
  const getStatusVariant = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "error":
        return "danger";
      case "warning":
        return "warning";
      case "info":
        return "info";
      default:
        return "danger";
    }
  };

  return (
    <div
      className='max-w-[680px] w-full mx-auto my-16'
      id='newsletter_promo_popup'
    >
      <div className='bg-[#241025] pt-2 pb-2 pl-8 pr-2 sm:!p-12'>
        <div className='flex flex-row items-center justify-between'>
          <m.div className='w-[58%] lg:w-full' {...fadeIn}>
            <h2 className="font-['Oxanium'] text-[32px] sm:text-[36px] text-white font-normal tracking-[-1px] leading-[1.2] mb-2 cursor-default">
              {t('blog:newsletter.title', 'B2B² Newsletter')}
            </h2>
            <p className='text-[14px] md:text-[12spx] text-white opacity-80 font-light font-rubik mb-6 cursor-default'>
              {t('blog:newsletter.subtitle', 'Každých 14 dní čerstvé novinky z marketingu')}
            </p>

            <Formik
              initialValues={{ email: "" }}
              validationSchema={validationSchema}
              onSubmit={async (values, actions) => {
                actions.setSubmitting(true);
                const response = await subscribeNewsletterUser(values);

                if (response.status === "success") {
                  resetForm(actions);
                  setFormStatus("success");
                  setSuccessMessage(
                    response.data?.message ||
                      t('blog:newsletter.success', 'Úspešne ste sa prihlásili na odber noviniek')
                  );
                  setTimeout(() => setFormStatus(null), 5000);
                } else {
                  actions.setSubmitting(false);
                  setFormStatus("error");

                  if (
                    response.message &&
                    response.message.includes("už prihlásený")
                  ) {
                    setFormStatus("warning");
                  }

                  setErrorMessage(
                    response.message ||
                      t('blog:newsletter.error', 'Nastala chyba pri odosielaní. Skúste prosím neskôr.')
                  );

                  setTimeout(() => setFormStatus(null), 5000);
                }
              }}
            >
              {({
                isSubmitting,
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
              }) => (
                <div className='relative mb-4'>
                  {/* Using a table-like layout approach for consistent sizing */}
                  <Form className='flex w-full' noValidate>
                    <div className='flex sm:flex-col w-full'>
                      {/* Input field taking most of the space */}
                      <div className='flex-grow sm:w-full'>
                        <input
                          type='email'
                          name='email'
                          id='email'
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={t('footer.emailPlaceholder')}
                          className='w-full h-[45px] py-3 px-3 box-border placeholder-inaqpurple'
                          style={{ display: "block" }} // Ensure block display
                        />
                        {errors.email && touched.email && (
                          <div className='text-red-300 text-xxs mt-2'>
                            {errors.email}
                          </div>
                        )}
                      </div>

                      {/* Button */}
                      <button
                        aria-label='subscribe'
                        type='submit'
                        className={`
                          h-[45px] 
                          w-[56px] sm:w-full
                          sm:mt-2
                          flex justify-center items-center 
                          bg-inaqgold hover:bg-[#82765e] active:bg-[#a19887] text-[#e6e6e6]
                          border-[1px] border-white hover:border-[#82765e] active:border-[#a19887] sm:border-none
                          ${isSubmitting ? " loading" : ""}
                        `}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className='spinner'></span>
                        ) : (
                          <i className='m-0 text-lg'>
                            <img
                              src={EnvelopeIcon}
                              alt=''
                              style={{
                                width: "1em",
                                height: "1em",
                              }}
                              className='envelope-icon'
                            />
                          </i>
                        )}
                      </button>
                    </div>
                  </Form>

                  <AnimatePresence>
                    {formStatus === "success" && (
                      <m.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className='absolute top-[115%] left-0 w-full'
                      >
                        <MessageBox
                          className='py-[8px] rounded-[2px] !text-[13px] font-rubik'
                          theme='message-box01'
                          variant='success'
                          message={successMessage}
                          dismissible={true}
                        />
                      </m.div>
                    )}
                    {(formStatus === "error" || formStatus === "warning") && (
                      <m.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className='absolute top-[115%] left-0 w-full !text-xs'
                      >
                        <MessageBox
                          className='py-[8px] rounded-[2px] !text-[13px] font-rubik'
                          theme='message-box01'
                          variant={getStatusVariant(formStatus)}
                          message={errorMessage}
                          dismissible={true}
                        />
                      </m.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </Formik>

            <p className='text-xxs font-rubik !leading-0 text-white font-light opacity-60 cursor-default'>
              {t('footer.newsletterTerms')}{' '}
              <a href={getLocalizedPath("/tos")} className="underline">{t('footer.privacyPolicy')}</a>.
            </p>
          </m.div>

          <div className='w-[38%] sm:hidden flex justify-end'>
            <img
              src='/assets/img/webp/b2b2_newsletter_promo.webp'
              alt='Newsletter subscription'
              className='w-full max-w-[180px] h-auto rounded-sm newsletter-sub-img'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPromo;
